import React, { useState, useEffect } from 'react';
import { useStepsForm } from 'sunflower-antd';
import {
  Form, Row, Select, Input, Button, Card, Space, Col, Slider, Typography, Switch, Steps, Checkbox, InputNumber
} from 'antd';
import {
  CheckOutlined, StopOutlined, FunnelPlotOutlined, ArrowLeftOutlined
} from '@ant-design/icons';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {
  hno3TemperatureFormula, h3po4TemperatureFormula, naohTemperatureFormula, h2o2TemperatureFormula,
  warmWatterConditionValidate, steamConditionValidate, nepApplies, ccConcentrationFormula
} from 'utils/formulas';
import FlexibleFeatures from 'utils/flexibleFeatures';
import { getCodeForFlexibleFeatures } from 'utils/codes';
import AuthenticatorWithEmail from 'utils/authentificator';
import {
  typeOptions, flexibleDiameterOptions, numberCyclesOptions, rightAngledOptions
} from '../constants';

const { Text, Title, Paragraph } = Typography;
const { Step } = Steps;
const { Option } = Select;

const Specifications = (props) => {
  const {
    finish, cancel, recipe, setFilteredFlexible, setIsGoodPractice
  } = props;
  const [hno3TemperatureMessage, setHno3TemperatureMessage] = useState('');
  const [h3po4TemperatureMessage, setH3po4TemperatureMessage] = useState('');
  const [naohTemperatureMessage, setNaohTemperatureMessage] = useState('');
  const [h2o2TemperatureMessage, setH2o2TemperatureMessage] = useState('');
  const [ccConcentrationMessage, setCcConcentrationMessage] = useState('');
  const [flexibleFeatures, setFlexibleFeatures] = useState([]);
  const [filteredFlexibleFeatures, setFilteredFlexibleFeatures] = useState([]);
  // const [goodPractice, setGoodPractice] = useState(true);
  // const [productCode, setProductCode] = useState({ type: 'T', practices: '5'})

  const codes = getCodeForFlexibleFeatures(recipe);

  const goodPracticeOptions = [
    { key: '1', value: 'wras', label: 'Wras' },
    { key: '2', value: 'bfr', label: 'Bfr' },
    { key: '3', value: 'usp_class_6', label: 'Usp class 6' },
  ];
  const environmentalConstraintsOptions = [
    { key: '1', value: 'alcool95', label: 'Alcool tirant 95%' },
    { key: '2', value: 'continuite_electrique', label: 'Continuité électrique' },
    { key: '3', value: 'atex', label: 'Atex' },
  ];

  const fetchFlexibleFeatures = async () => {
    try {
      const list = await FlexibleFeatures(codes);
      // eslint-disable-next-line no-underscore-dangle
      setFlexibleFeatures(list.data.listFlexibleFeatures.items.filter((feature) => !feature._deleted));
    } catch (e) {
      console.error(e.toString());
    }
  };

  useEffect(async () => {
    if (recipe && codes) {
      await fetchFlexibleFeatures();
    }
  }, []);

  const {
    form,
    current,
    gotoStep,
    stepsProps,
    formProps,
    submit,
    formLoading,
  } = useStepsForm({
    async submit(values) {
      await new Promise(r => setTimeout(r, 1000));
      // setFilteredFlexible(filteredFlexibleFeatures);
      finish(values);
      await new Promise(r => setTimeout(r, 1000));
      return 'ok';
    },
    total: 6,
  });

  const { getFieldValue, getFieldsValue } = form;

  const onChange = (newFields) => {
    form.setFields(newFields);
  };

  const filterFlexibleFeatures = async (flexibleName, formValues) => {
    let filtered = flexibleFeatures.filter(flexible => flexibleName.includes(flexible.title.toUpperCase()));
    if (formValues.warmWatter) {
      // eslint-disable-next-line array-callback-return,consistent-return
      filtered = filtered.map(flexible => {
        if (!warmWatterConditionValidate(
          formValues.warmWatterTemperature,
          formValues.warmWatterDuration,
          flexible
        )) {
          return flexible;
        }
      });
    }
    if (formValues.steam) {
      // eslint-disable-next-line array-callback-return,consistent-return
      filtered = filtered.filter(flexible => {
        if (!steamConditionValidate(
          formValues.steamTemperature,
          formValues.steamDuration,
          flexible
        )) {
          return flexible;
        }
      });
    }
    setFilteredFlexibleFeatures(filtered);
  };

  const onNepChosen = async (values) => {
    const entries = Object.entries(values);
    const trueValues = entries.filter(val => val[1] === true);
    const nep = {};
    trueValues.forEach(value => {
      if (value[1] === true) {
        nep[value[0]] = {};
        const concentration = entries.filter(val => val[0] === `${value[0]}Concentration`);
        if (concentration[0] && concentration[0][1]) {
          // eslint-disable-next-line prefer-destructuring
          nep[value[0]].concentration = concentration[0][1];
        }
        const duration = entries.filter(val => val[0] === `${value[0]}Duration`);
        if (duration[0] && duration[0][1]) {
          // eslint-disable-next-line prefer-destructuring
          nep[value[0]].duration = duration[0][1];
        }
        const temperature = entries.filter(val => val[0] === `${value[0]}Temperature`);
        if (temperature[0] && temperature[0][1]) {
          // eslint-disable-next-line prefer-destructuring
          nep[value[0]].temperature = temperature[0][1];
        }
      }
    });

    let flexibleNames = [];
    const intersection = ['naoh', 'h2o2', 'c2h403'];
    // const excludes = ['warmWatter', 'steam'];
    const excludes = [];

    Object.entries(nep).forEach((value) => {
      if (!excludes.includes(value[0])) {
        const arr = nepApplies[value[0]].apply(value[1], [value[1].temperature, value[1].concentration]);
        if (arr) {
          if (intersection.includes(value[0])) {
            if (_.isEmpty(flexibleNames)) {
              flexibleNames = flexibleNames.concat(arr);
            } else {
              flexibleNames = flexibleNames.filter(x => arr.includes(x));
            }
          } else {
            flexibleNames = flexibleNames.concat(arr);
          }
        }
      }
    });
    await filterFlexibleFeatures(flexibleNames, values);
    gotoStep(current + 1);
  };

  const onConditionsChosen = (values, flexes) => {
    const flexibleFromForm = flexes.filter(element => element !== undefined);
    const filtered = flexibleFromForm.filter(flexible => {
      const pressure = JSON.parse(flexible?.pression_service);
      const depressure = JSON.parse(flexible?.depression);
      const { temperatureMin, temperatureMax } = flexible;
      return values.pressure <= pressure.value
        && values.depressure <= depressure.value
        && (values.useTemperature >= temperatureMin && values.useTemperature <= temperatureMax);
    });
    setFilteredFlexible(filtered);
  };

  const onStandardChosen = (values) => {
    let filtered = filteredFlexibleFeatures;

    const goodPracticeOptionsIntersection = goodPracticeOptions.filter(x => values?.standards?.includes(x.value));
    const environmentalConstraintsOptionsIntersections = environmentalConstraintsOptions.filter(x => values?.environmentalConstraints?.includes(x.value));
    // if (!values?.goodPractice
    //   || _.isEmpty(goodPracticeOptionsIntersection)
    //   || _.isEmpty(environmentalConstraintsOptionsIntersections)) {
    //   setGoodPractice(false);
    //   setIsGoodPractice(false);
    // }
    if (!_.isEmpty(goodPracticeOptionsIntersection)) {
      // eslint-disable-next-line no-return-assign
      goodPracticeOptionsIntersection.forEach(item => filtered = filtered.filter(flexible => flexible[item.value]));
    }
    if (!_.isEmpty(environmentalConstraintsOptionsIntersections)) {
      // eslint-disable-next-line no-return-assign
      environmentalConstraintsOptionsIntersections.forEach(item => filtered = filtered.filter(flexible => flexible[item.value]));
    }

    setFilteredFlexibleFeatures([]);
    return filtered;
  };

  function checkFlexibleDnCompatibilities(flexibleDn, rDn) {
    return (flexibleDn === 38 && [38, 40].includes(rDn))
      || ([50, 51].includes(flexibleDn) && [50, 51].includes(rDn))
      || ([63].includes(flexibleDn) && [63, 65].includes(rDn));
  }

  function getFlexibleDnByCodeAndLabel(code, label, optionsList) {
    const options = optionsList.find(option => option.label === label);
    if (options) {
      const option = options.doptions.find(opt => opt.value === code);
      return option.label;
    }
    return false;
  }

  const onFlexibleDetailsChosen = (values) => {
    const dn1 = getFlexibleDnByCodeAndLabel(values?.r1Diameter, values?.r1Type, typeOptions);
    const dn2 = getFlexibleDnByCodeAndLabel(values?.r2Diameter, values?.r2Type, typeOptions);
    if (dn1 !== dn2
      || values?.r1RightAngled !== 0
      || values?.r2RightAngled !== 0
      || !checkFlexibleDnCompatibilities(values?.diameter, dn1)
    ) {
      // setGoodPractice(false);
      setIsGoodPractice(false);
    }
  };

  const handleFinalFormStep = values => {
    const elements = onStandardChosen(values);
    onConditionsChosen(values, elements);
    onFlexibleDetailsChosen(values);
    submit().then(result => {
      if (result === 'ok') {
        console.log('form fired');
      }
    });
  };

  const getCardFields = (cardTitle, d, check = 'ch', dyn) => {
    const span = d.length > 2 ? 6 : 10;
    const bg = dyn ? { backgroundColor: '#FFF7F1' } : {};
    const pad = dyn ? {} : { padding: 0 };
    return (
      <Card
        title={cardTitle}
        headStyle={{ paddingBottom: 0, margin: 0, ...bg }}
        bodyStyle={{ paddingBottom: 0, ...pad }}
        size="small"
        extra={(
          <Form.Item
            shouldUpdate
            name={check}
          >
            <Switch checked={dyn} />
          </Form.Item>
        )}
      >
        { dyn && (
          <Row type="flex" align="space-between">
            {d.map(field => (
              <Col xs={24} sm={24} md={span} lg={span} xl={span}>
                <Text>
                  {field.label}
                  :
                  {' '}
                  {field.func ?? field.defaultVal}
                </Text>
                {' '}
                {field.suffix}
                <Form.Item
                  shouldUpdate
                  validateStatus="warning"
                  name={field.name}
                  rules={field.rules}
                  style={{ marginBottom: 0 }}
                >
                  <Slider
                    min={field.min}
                    max={field.max}
                    step={field?.step}
                  />
                </Form.Item>
                {field.infos && (
                  <Text type="warning">{field.infos}</Text>
                )}
              </Col>
            ))}
          </Row>
        )}
      </Card>
    );
  };

  function setState(res, stateFunc) {
    const message = res ? 'Conditions sévères nous contacter' : '';
    stateFunc(message);
  }

  function validateHno3TemperatureMessage() {
    const res = hno3TemperatureFormula(getFieldValue('hno3Temperature'));
    setState(res, setHno3TemperatureMessage);
  }

  function validateHpo4TemperatureMessage() {
    const res = h3po4TemperatureFormula(getFieldValue('h3po4Temperature'));
    setState(res, setH3po4TemperatureMessage);
  }

  function validateCcConcentrationMessage() {
    const res = ccConcentrationFormula(getFieldValue('ccConcentration'));
    setState(res, setCcConcentrationMessage);
  }

  function validateNaoh4TemperatureMessage() {
    const res = naohTemperatureFormula(getFieldValue('naohTemperature'));
    setState(res, setNaohTemperatureMessage);
  }

  function validateH2o2TemperatureMessage() {
    const res = h2o2TemperatureFormula(getFieldValue('h2o2Temperature'),);
    setState(res, setH2o2TemperatureMessage);
  }

  const getFieldOptions = (value) => {
    const key = typeOptions.find(item => item.value === value);
    return key.doptions;
  };

  const formList = [
    <>
      <Title level={4} style={{ textAlign: 'center', marginTop: '30px' }}>Titre</Title>
      <Card style={{ marginBottom: '20px', marginTop: '10px' }}>
        <Form.Item
          required
          shouldUpdate
          name="name"
          label="Titre du cachier des charges"
          labelAlign="left"
          rules={[
            {
              required: true,
              message: 'Champs requis',
            },
          ]}
        >
          <Input />
        </Form.Item>
      </Card>
      <Row justify="center">
        <Space wrap align="start" size="large" style={{ marginTop: '20px' }}>
          <Col xs={24} sm={24} md={12} lg={12} xl={12}>
            <Form.Item>
              <Button
                loading={formLoading}
                type="primary"
                onClick={() => gotoStep(current + 1)}
                icon={<CheckOutlined />}
              >
                Valider
              </Button>
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12}>
            <Form.Item>
              <Button onClick={() => cancel()} icon={<StopOutlined />}>Annuler</Button>
            </Form.Item>
          </Col>
        </Space>
      </Row>
    </>,
    <>
      <Form.Item
        noStyle
        shouldUpdate
      >
        {() => (
          <>
            <Title level={4} style={{ textAlign: 'center', marginTop: '30px' }}>
              <FunnelPlotOutlined />
              {' '}
              Conditions NEP
            </Title>
            {getCardFields('Eau chaude:', [
              {
                label: 'Durée',
                func: getFieldValue('warmWatterDuration'),
                name: 'warmWatterDuration',
                defaultVal: 28,
                suffix: 'mn',
                min: 0,
                max: 35,
                step: 1,
                rules: [
                  {
                    type: 'number',
                    max: 30,
                    message: 'Conditions sévères nous contacter',
                    warningOnly: true
                  }]
              },
              {
                label: 'Température',
                func: getFieldValue('warmWatterTemperature'),
                name: 'warmWatterTemperature',
                defaultVal: 69,
                suffix: '°C',
                min: 0,
                max: 100,
                step: 1,
                rules: [
                  {
                    type: 'number',
                    max: 95,
                    message: 'Conditions sévères nous contacter',
                    warningOnly: true
                  }]
              }
            ], 'warmWatter', getFieldValue('warmWatter'))}
            {getCardFields('Vapeur circuit ouvert:', [
              {
                label: 'Durée',
                func: getFieldValue('steamDuration'),
                name: 'steamDuration',
                defaultVal: 30,
                suffix: 'mn',
                min: 0,
                max: 35,
                step: 1,
                rules: [
                  {
                    type: 'number',
                    max: 30,
                    message: 'Conditions sévères nous contacter',
                    warningOnly: true
                  }]
              },
              {
                label: 'Température',
                func: getFieldValue('steamTemperature'),
                name: 'steamTemperature',
                defaultVal: 67,
                suffix: '°C',
                min: 0,
                max: 135,
                step: 1,
                rules: [
                  {
                    type: 'number',
                    max: 130,
                    message: 'Conditions sévères nous contacter',
                    warningOnly: true
                  }]
              }
            ], 'steam', getFieldValue('steam'))}
            {getCardFields('Acide nitrique (HNO3):', [
              {
                label: 'Concentration',
                func: getFieldValue('hno3Concentration'),
                name: 'hno3Concentration',
                defaultVal: 1.8,
                suffix: '%',
                min: 0,
                max: 3.2,
                step: 0.1,
                rules: [
                  {
                    type: 'number',
                    max: 3.0,
                    message: 'Conditions sévères nous contacter',
                    warningOnly: true
                  }]
              },
              {
                label: 'Durée',
                func: getFieldValue('hno3Duration'),
                name: 'hno3Duration',
                defaultVal: 11,
                suffix: 'mn',
                min: 0,
                max: 20,
                step: 1,
                rules: [
                  {
                    type: 'number',
                    max: 15,
                    message: 'Conditions sévères nous contacter',
                    warningOnly: true
                  },
                ]
              },
              {
                label: 'Température',
                func: getFieldValue('hno3Temperature'),
                name: 'hno3Temperature',
                defaultVal: 39,
                suffix: '°C',
                min: 0,
                max: 90,
                step: 1,
                infos: hno3TemperatureMessage
              }
            ], 'hno3', getFieldValue('hno3'))}
            {getCardFields('Acide phosphorique (H3PO4):', [
              {
                label: 'Concentration',
                func: getFieldValue('h3po4Concentration'),
                name: 'h3po4Concentration',
                defaultVal: 2,
                suffix: '%',
                min: 0,
                max: 3.2,
                step: 0.1,
                rules: [
                  {
                    type: 'number',
                    max: 3.0,
                    message: 'Conditions sévères nous contacter',
                    warningOnly: true
                  }]
              },
              {
                label: 'Durée',
                func: getFieldValue('h3po4Duration'),
                name: 'h3po4Duration',
                defaultVal: 10,
                suffix: 'mn',
                min: 0,
                max: 20,
                step: 1,
                rules: [
                  {
                    type: 'number',
                    max: 15,
                    message: 'Conditions sévères nous contacter',
                    warningOnly: true
                  }]
              },
              {
                label: 'Température',
                func: getFieldValue('h3po4Temperature'),
                name: 'h3po4Temperature',
                defaultVal: 48,
                suffix: '°C',
                min: 0,
                max: 100,
                step: 1,
                infos: h3po4TemperatureMessage
              }
            ], 'h3po4', getFieldValue('h3po4'))}
            {getCardFields('Composés chlorés  ( chlore Cl acide chlorydrique HCI , hypochlorite de sodium NaCIO…):', [
              {
                label: 'Concentration',
                func: getFieldValue('ccConcentration'),
                name: 'ccConcentration',
                defaultVal: 0.9,
                suffix: '%',
                min: 0,
                max: 5.5,
                step: 0.1,
                infos: ccConcentrationMessage
              },
              {
                label: 'Durée',
                func: getFieldValue('ccDuration'),
                name: 'ccDuration',
                defaultVal: 15,
                suffix: 'mn',
                min: 0,
                max: 20,
                step: 1,
                rules: [
                  {
                    type: 'number',
                    max: 15,
                    message: 'Conditions sévères nous contacter',
                    warningOnly: true
                  }]
              },
              {
                label: 'Température',
                func: getFieldValue('ccTemperature'),
                name: 'ccTemperature',
                defaultVal: 70,
                suffix: '°C',
                min: 0,
                max: 75,
                step: 1,
                rules: [
                  {
                    type: 'number',
                    max: 70,
                    message: 'Conditions sévères nous contacter',
                    warningOnly: true
                  }]
              }
            ], 'cc', getFieldValue('cc'))}
            {getCardFields('Hydroxyde de sodium (NaOH):', [
              {
                label: 'Concentration',
                func: getFieldValue('naohConcentration'),
                name: 'naohConcentration',
                defaultVal: 2.2,
                suffix: '%',
                min: 0,
                max: 5.5,
                step: 0.1,
                rules: [
                  {
                    type: 'number',
                    max: 5,
                    message: 'Conditions sévères nous contacter',
                    warningOnly: true
                  }]
              },
              {
                label: 'Durée',
                func: getFieldValue('naohDuration'),
                name: 'naohDuration',
                defaultVal: 15,
                suffix: 'mn',
                min: 0,
                max: 20,
                step: 1,
                rules: [
                  {
                    type: 'number',
                    max: 15,
                    message: 'Conditions sévères nous contacter',
                    warningOnly: true
                  }]
              },
              {
                label: 'Température',
                func: getFieldValue('naohTemperature'),
                name: 'naohTemperature',
                defaultVal: 61,
                suffix: '°C',
                min: 0,
                max: 85,
                step: 1,
                infos: naohTemperatureMessage
              }
            ], 'naoh', getFieldValue('naoh'))}
            {getCardFields('Peroxyde d\'hydrogène ( H2O2):', [
              {
                label: 'Concentration',
                func: getFieldValue('h2o2Concentration'),
                name: 'h2o2Concentration',
                defaultVal: 2.5,
                suffix: '%',
                min: 0,
                max: 3.5,
                step: 0.1,
                rules: [
                  {
                    type: 'number',
                    max: 3.0,
                    message: 'Conditions sévères nous contacter',
                    warningOnly: true
                  }]
              },
              {
                label: 'Durée',
                func: getFieldValue('h2o2Duration'),
                name: 'h2o2Duration',
                defaultVal: 14,
                suffix: 'mn',
                min: 0,
                max: 20,
                step: 1,
                rules: [
                  {
                    type: 'number',
                    max: 15,
                    message: 'Conditions sévères nous contacter',
                    warningOnly: true
                  }]
              },
              {
                label: 'Température',
                func: getFieldValue('h2o2Temperature'),
                name: 'h2o2Temperature',
                defaultVal: 38,
                suffix: '°C',
                min: 0,
                max: 90,
                step: 1,
                infos: h2o2TemperatureMessage
              }
            ], 'h2o2', getFieldValue('h2o2'))}
            {getCardFields('Acide peracétique ( C2H4O3 ):', [
              {
                label: 'Concentration',
                func: getFieldValue('c2h4o3Concentration'),
                name: 'c2h4o3Concentration',
                defaultVal: 0.3,
                suffix: '%',
                min: 0,
                max: 1.3,
                step: 0.1,
                rules: [
                  {
                    type: 'number',
                    max: 1.0,
                    message: 'Conditions sévères nous contacter',
                    warningOnly: true
                  }]
              },
              {
                label: 'Durée',
                func: getFieldValue('c2h4o3Duration'),
                name: 'c2h4o3Duration',
                defaultVal: 15,
                suffix: 'mn',
                min: 0,
                max: 20,
                step: 1,
                rules: [
                  {
                    type: 'number',
                    max: 15,
                    message: 'Conditions sévères nous contacter',
                    warningOnly: true
                  }]
              },
              {
                label: 'Température',
                func: getFieldValue('c2h4o3Temperature'),
                name: 'c2h4o3Temperature',
                defaultVal: 15,
                suffix: '°C',
                min: 0,
                max: 55,
                step: 1,
                rules: [
                  {
                    type: 'number',
                    max: 50,
                    message: 'Conditions sévères nous contacter',
                    warningOnly: true
                  }]
              }
            ], 'c2h4o3', getFieldValue('c2h4o3'))}
          </>
        )}
      </Form.Item>
      <Row justify="center">
        <Space wrap align="start" size="large" style={{ marginTop: '20px' }}>
          <Col xs={24} sm={24} md={12} lg={12} xl={12}>
            <Form.Item>
              <Button
                type="primary"
                onClick={() => onNepChosen(getFieldsValue(true))}
                loading={formLoading}
                icon={<CheckOutlined />}
              >
                Valider
              </Button>
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12}>
            <Form.Item>
              <Button
                type="primary"
                onClick={() => gotoStep(current - 1)}
                icon={<ArrowLeftOutlined />}
              >
                Retour
              </Button>
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12}>
            <Form.Item>
              <Button onClick={() => cancel()} icon={<StopOutlined />}>Annuler</Button>
            </Form.Item>
          </Col>
        </Space>
      </Row>
    </>,
    <>
      <Title level={4} style={{ textAlign: 'center', marginTop: '30px' }}>Cycles NEP</Title>
      <Card style={{ marginBottom: '20px', marginTop: '10px' }}>
        <Form.Item
          noStyle
          shouldUpdate
        >
          {() => (
            <>
              <Form.Item
                shouldUpdate
                label="nombre de cycle"
                name="numberCycles"
                rules={[
                  {
                    required: true,
                    message: 'Champs requis',
                  },
                ]}
              >
                <Select options={numberCyclesOptions} />
              </Form.Item>
              {getFieldValue('numberCycles') && getFieldValue('numberCycles') > 2 && (
                <Form.Item
                  shouldUpdate
                  label="validation des conditions des cycles des NEP ?"
                  name="NepNeedValidation"
                  rules={[
                    {
                      required: true,
                      message: 'Champs requis',
                    },
                  ]}
                >
                  <Select>
                    <Option value="yes">Oui</Option>
                    <Option value="no">Non</Option>
                  </Select>
                </Form.Item>
              )}
            </>
          )}
        </Form.Item>
      </Card>
      <Row justify="center">
        <Space wrap align="start" size="large" style={{ marginTop: '20px' }}>
          <Col xs={24} sm={24} md={12} lg={12} xl={12}>
            <Form.Item>
              <Button
                type="primary"
                loading={formLoading}
                onClick={() => gotoStep(current + 1)}
                icon={<CheckOutlined />}
              >
                Valider
              </Button>
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12}>
            <Form.Item>
              <Button
                type="primary"
                onClick={() => gotoStep(current - 1)}
                icon={<ArrowLeftOutlined />}
              >
                Retour
              </Button>
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12}>
            <Form.Item>
              <Button onClick={() => cancel()} icon={<StopOutlined />}>Annuler</Button>
            </Form.Item>
          </Col>
        </Space>
      </Row>
    </>,
    <>
      <Form.Item
        noStyle
        shouldUpdate
      >
        {() => (
          <>
            <Title level={4} style={{ textAlign: 'center', marginTop: '30px' }}>Informations utilisation</Title>
            <Card style={{ marginBottom: '20px', marginTop: '10px' }}>
              <Row type="flex" align="space-between">
                <Col xs={24} sm={24} md={6} lg={6} xl={6}>
                  <Text>
                    Pression
                    :
                    {' '}
                    {getFieldValue('pressure')}
                  </Text>
                  {' '}
                  bar
                  <Form.Item
                    shouldUpdate
                    validateStatus="warning"
                    name="pressure"
                    suffix="bar"
                    style={{ marginBottom: 0 }}
                    rules={[
                      {
                        required: true,
                        message: 'Champs requis',
                      },
                    ]}
                  >
                    <Slider
                      min={0}
                      max={15}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={6} lg={6} xl={6}>
                  <Text>
                    Dépression
                    :
                    {' '}
                    {getFieldValue('depressure')}
                  </Text>
                  {' '}
                  bar
                  <Form.Item
                    shouldUpdate
                    validateStatus="warning"
                    name="depressure"
                    suffix="bar"
                    style={{ marginBottom: 0 }}
                    rules={[
                      {
                        required: true,
                        message: 'Champs requis',
                      },
                    ]}
                  >
                    <Slider
                      min={0}
                      max={0.9}
                      step={0.1}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={6} lg={6} xl={6}>
                  <Text>
                    Température d’utilisation
                    :
                    {' '}
                    {getFieldValue('useTemperature')}
                  </Text>
                  {' '}
                  °C
                  <Form.Item
                    shouldUpdate
                    validateStatus="warning"
                    name="useTemperature"
                    suffix="°C"
                    style={{ marginBottom: 0 }}
                    rules={[
                      {
                        required: true,
                        message: 'Champs requis',
                      },
                    ]}
                  >
                    <Slider
                      min={-30}
                      max={100}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </>
        )}
      </Form.Item>
      <Row justify="center">
        <Space wrap align="start" size="large" style={{ marginTop: '20px' }}>
          <Col xs={24} sm={24} md={12} lg={12} xl={12}>
            <Form.Item>
              <Button
                type="primary"
                onClick={() => gotoStep(current + 1)}
                loading={formLoading}
                icon={<CheckOutlined />}
              >
                Valider
              </Button>
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12}>
            <Form.Item>
              <Button
                type="primary"
                onClick={() => gotoStep(current - 1)}
                icon={<ArrowLeftOutlined />}
              >
                Retour
              </Button>
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12}>
            <Form.Item>
              <Button onClick={() => cancel()} icon={<StopOutlined />}>Annuler</Button>
            </Form.Item>
          </Col>
        </Space>
      </Row>
    </>,
    <>
      <Title level={4} style={{ textAlign: 'center', marginTop: '30px' }}>Normes</Title>
      <Card style={{ marginBottom: '20px', marginTop: '10px' }}>
        <Form.Item
          name="hasGoodPractice"
          valuePropName="checked"
          label="Je valide que l’application est impactée par les trois impératifs normatifs à respecter
            (Bonnes pratiques, UE règlementation, FDA règlementation)"
        >
          <Checkbox />
        </Form.Item>
        <Form.Item
          name="standards"
          label="Je valide que l’application est impactée par un des trois impératifs normatifs spécifique suivants"
        >
          <Checkbox.Group options={goodPracticeOptions} />
        </Form.Item>
        <Form.Item
          name="environmentalConstraints"
          label="Je valide que l’application est impactée par une des trois contraintes environnementales suivantes"
        >
          <Checkbox.Group options={environmentalConstraintsOptions} />
        </Form.Item>
        <Paragraph type="danger" style={{ textAlign: 'right', fontWeight: 'bold' }}>Un cahier des charges trop contraignant limite le choix des flexibles</Paragraph>
      </Card>
      <Row justify="center">
        <Space wrap align="start" size="large" style={{ marginTop: '20px' }}>
          <Col xs={24} sm={24} md={12} lg={12} xl={12}>
            <Form.Item>
              <Button
                type="primary"
                loading={formLoading}
                onClick={() => gotoStep(current + 1)}
                icon={<CheckOutlined />}
              >
                Valider
              </Button>
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12}>
            <Form.Item>
              <Button
                type="primary"
                onClick={() => gotoStep(current - 1)}
                icon={<ArrowLeftOutlined />}
              >
                Retour
              </Button>
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={12} lg={12} xl={12}>
            <Form.Item>
              <Button onClick={() => cancel()} icon={<StopOutlined />}>Annuler</Button>
            </Form.Item>
          </Col>
        </Space>
      </Row>
    </>,
    <>
      <>
        <Form.Item
          noStyle
          shouldUpdate
        >
          {() => (
            <>
              <Title level={4} style={{ textAlign: 'center', marginTop: '30px' }}>Informations flexible</Title>
              <Card
                title="Raccord 1"
                style={{ marginBottom: '20px', marginTop: '20px' }}
                headStyle={{ backgroundColor: '#FFF7F1' }}
              >
                <Form.Item
                  required
                  shouldUpdate
                  name="r1Type"
                  label="Type"
                  labelAlign="left"
                  rules={[
                    {
                      required: true,
                      message: 'Champs requis',
                    },
                  ]}
                >
                  <Select options={typeOptions} />
                </Form.Item>
                {getFieldValue('r1Type') && (
                  <Form.Item
                    required
                    shouldUpdate={
                      (prevValues, curValues) => {
                        if (prevValues.r1Type !== curValues.r1Type) {
                          form.resetFields(['r1Diameter']);
                        }
                      }
                    }
                    name="r1Diameter"
                    label="Diamètre"
                    labelAlign="left"
                    rules={[
                      {
                        required: true,
                        message: 'Champs requis',
                      },
                    ]}
                  >
                    <Select options={getFieldOptions(getFieldValue('r1Type'))} />
                  </Form.Item>
                )}
                <Form.Item
                  required
                  shouldUpdate
                  name="r1RightAngled"
                  label="Le raccord est-il coudé ?"
                  labelAlign="left"
                  rules={[
                    {
                      required: true,
                      message: 'Champs requis',
                    },
                  ]}
                >
                  <Select options={rightAngledOptions} />
                </Form.Item>
                <Form.Item
                  shouldUpdate
                  name="r1asherNumber"
                  label="Nombre de rondelles"
                  labelAlign="left"
                >
                  <InputNumber min={0} defaultValue={0} />
                </Form.Item>
                {getFieldValue('r1asherNumber') > 0 && (
                  <Form.Item
                    required
                    name="r1WasherColor"
                    label="Couleur des rondelles"
                    labelAlign="left"
                    rules={[
                      {
                        required: true,
                        message: 'Champs requis',
                      },
                    ]}
                  >
                    <Select>
                      <Option value="yellow">Jaune</Option>
                      <Option value="red">Rouge</Option>
                    </Select>
                  </Form.Item>
                )}
              </Card>
              <Card
                title="Raccord 2"
                style={{ marginBottom: '20px', marginTop: '20px' }}
                headStyle={{ backgroundColor: '#FFF7F1' }}
              >
                <Form.Item
                  required
                  shouldUpdate
                  name="r2Type"
                  label="Type"
                  labelAlign="left"
                  rules={[
                    {
                      required: true,
                      message: 'Champs requis',
                    },
                  ]}
                >
                  <Select options={typeOptions} />
                </Form.Item>
                {getFieldValue('r2Type') && (
                  <Form.Item
                    required
                    shouldUpdate={
                      (prevValues, curValues) => {
                        if (prevValues.r2Type !== curValues.r2Type) {
                          form.resetFields(['r2Diameter']);
                        }
                      }
                    }
                    name="r2Diameter"
                    label="Diamètre"
                    labelAlign="left"
                    rules={[
                      {
                        required: true,
                        message: 'Champs requis',
                      },
                    ]}
                  >
                    <Select options={getFieldOptions(getFieldValue('r2Type'))} />
                  </Form.Item>
                )}
                <Form.Item
                  required
                  shouldUpdate
                  name="r2RightAngled"
                  label="Le raccord est-il coudé ?"
                  labelAlign="left"
                  rules={[
                    {
                      required: true,
                      message: 'Champs requis',
                    },
                  ]}
                >
                  <Select options={rightAngledOptions} />
                </Form.Item>
                <Form.Item
                  shouldUpdate
                  name="r2asherNumber"
                  label="Nombre de rondelles"
                  labelAlign="left"
                >
                  <InputNumber min={0} defaultValue={0} />
                </Form.Item>
                {getFieldValue('r2asherNumber') > 0 && (
                  <Form.Item
                    required
                    shouldUpdate
                    name="r2WasherColor"
                    label="Couleur des rondelles"
                    labelAlign="left"
                    rules={[
                      {
                        required: true,
                        message: 'Champs requis',
                      },
                    ]}
                  >
                    <Select>
                      <Option value="yellow">Jaune</Option>
                      <Option value="red">Rouge</Option>
                    </Select>
                  </Form.Item>
                )}
              </Card>
              <Card
                title="Tuyau"
                style={{ marginBottom: '20px', marginTop: '20px' }}
                headStyle={{ backgroundColor: '#FFF7F1' }}
              >
                <Form.Item
                  required
                  shouldUpdate
                  name="length"
                  label="Longueur du flexible de joint à joint en milimètres"
                  labelAlign="left"
                  rules={[
                    {
                      required: true,
                      message: 'Champs requis',
                    },
                  ]}
                >
                  <Input suffix="mm" type="number" />
                </Form.Item>
                <Form.Item
                  required
                  shouldUpdate
                  name="diameter"
                  label="Diamètre"
                  labelAlign="left"
                  rules={[
                    {
                      required: true,
                      message: 'Champs requis',
                    },
                  ]}
                >
                  <Select options={flexibleDiameterOptions} />
                </Form.Item>
              </Card>
            </>
          )}
        </Form.Item>
        <Row justify="center">
          <Space wrap align="start" size="large" style={{ marginTop: '20px' }}>
            <Col xs={24} sm={24} md={12} lg={12} xl={12}>
              <Form.Item>
                <Button
                  type="primary"
                  loading={formLoading}
                  onClick={() => handleFinalFormStep(getFieldsValue(true))}
                  icon={<CheckOutlined />}
                >
                  Valider
                </Button>
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12} lg={12} xl={12}>
              <Form.Item>
                <Button
                  type="primary"
                  onClick={() => gotoStep(current - 1)}
                  icon={<ArrowLeftOutlined />}
                >
                  Retour
                </Button>
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12} lg={12} xl={12}>
              <Form.Item>
                <Button onClick={() => cancel()} icon={<StopOutlined />}>Annuler</Button>
              </Form.Item>
            </Col>
          </Space>
        </Row>
      </>
    </>
  ];

  return (
    <AuthenticatorWithEmail>
      <Row justify="center" type="flex" style={{ marginTop: '40px', marginBottom: '40px' }}>
        <Col span={23}>
          <Steps {...stepsProps}>
            <Step title="Titre" disabled />
            <Step title="NEP" disabled />
            <Step title="Cycles" disabled />
            <Step title="Utilisation" disabled />
            <Step title="Normes" disabled />
            <Step title="Flexible" disabled />
          </Steps>
        </Col>
      </Row>
      <Row justify="center" type="flex">
        <Col span={24}>
          <Form
            {...formProps}
            layout="horizontal"
            initialValues={{
              warmWatterDuration: 28,
              warmWatterTemperature: 69,
              steamDuration: 30,
              steamTemperature: 67,
              hno3Concentration: 1.8,
              hno3Temperature: 39,
              hno3Duration: 11,
              h3po4Concentration: 2,
              h3po4Temperature: 48,
              h3po4Duration: 10,
              ccConcentration: 1,
              ccTemperature: 70,
              ccDuration: 15,
              naohConcentration: 2.2,
              naohTemperature: 61,
              naohDuration: 15,
              h2o2Concentration: 2.5,
              h2o2Duration: 14,
              h2o2Temperature: 38,
              c2h4o3Concentration: 0.3,
              c2h4o3Duration: 15,
              c2h4o3Temperature: 15
            }}
            // eslint-disable-next-line no-shadow
            onFieldsChange={(_, allFields) => {
              // eslint-disable-next-line no-unused-expressions,no-sequences
              validateHno3TemperatureMessage(),
              validateHpo4TemperatureMessage(),
              validateNaoh4TemperatureMessage(),
              validateH2o2TemperatureMessage(),
              validateCcConcentrationMessage(),
              onChange(allFields);
            }}
          >
            {formList[current]}
          </Form>
        </Col>
      </Row>
    </AuthenticatorWithEmail>
  );
};

Specifications.propTypes = {
  finish: PropTypes.func.isRequired,
  setFilteredFlexible: PropTypes.func,
  cancel: PropTypes.func,
  recipe: PropTypes.shape({
    name: PropTypes.string,
    userId: PropTypes.string,
    ingredients: PropTypes.shape({
      condition: PropTypes.string,
      ingredient: PropTypes.string,
      themes: PropTypes.string
    })
  }),
  setIsGoodPractice: PropTypes.func,
};

export default Specifications;
